import type { Landmarks, HandId } from '@store/handStore';
import type { GestureMode } from '@store/gestureStore';
import {
  FINGER_TIPS,
  LM,
  dist,
  fingerCurl,
} from '@engine/hand/landmarks';

/**
 * Classifies a single-hand posture into a `GestureMode` (idle / pluck / bend).
 *
 * BEND (index/middle/ring only — thumb is a pluck finger): any one of 3
 * joint layers (DIP→TIP, PIP→DIP, MCP→PIP) passing qualifies as bend —
 * multi-layer voting is robust to TIPs drifting off-frame.
 *
 * PLUCK: tips spread, at least one finger extended. `PluckDetector`
 *        handles the dynamic strike downstream via velocity gates.
 */

/** Minimum X-span of index/middle/ring tips to count as "fingers spread" (not pinched). */
const PLAY_MIN_X_SPREAD = 0.04;

/**
 * Minimum ratio of palm width (INDEX_MCP → PINKY_MCP) to palm height
 * (WRIST → MIDDLE_MCP). Below this threshold the hand is edge-on and we
 * refuse to classify. Tuned from calibration 2026-04-21: play_stance p5
 * ≈ 0.371, pluck p5 ≈ 0.355, bend_still p5 ≈ 0.408. Previous 0.55 was
 * rejecting ALL legitimate play stances — 0.32 admits all valid play
 * poses while still rejecting truly edge-on hands where palmW collapses. */
const PALM_FACING_RATIO = 0.26;

/** Hand must be roughly upright — fingers pointing up, not sideways.
 *  Compare |wrist.y − middleMcp.y| to |wrist.x − middleMcp.x|; the Y
 *  component must dominate by at least this ratio. Relaxed from 1.5 to
 *  1.0 (45° tilt allowed) after multiple sessions showed valid bend
 *  poses getting classified idle because the hand was slightly rotated. */
const HAND_UPRIGHT_Y_OVER_X = 1.0;

/**
 * Depth gate for bend, RELATIVE to this hand's own peak palm width so it
 * adapts to small vs large hands. peakPalmW is the (slowly decaying) max
 * palmW observed on this hand; current palmW must reach ≥ BEND_DEPTH_FRAC
 * of that peak to count as "close to the instrument". Pulling the hand
 * back shrinks palmW below the fraction → bend releases.
 */
const BEND_DEPTH_FRAC = 0.35;
/** Decay of peakPalmW per frame. 0.9998 ≈ half-life ~2 minutes @ 30 fps —
 *  persists through brief retractions, eventually relaxes for a new user. */
const PEAK_PALM_DECAY = 0.9998;

/** How loose the horizontal-alignment check is. The user's spec —
 *  "khá song song với dây, không cần trên đường quá thẳng" — just wants
 *  the three upper joints to sit on roughly the same row, not a perfect
 *  line. 2× the raw bendAlignThreshold slider gives that forgiving feel. */
const BEND_Y_ALIGN_RELAX = 2.0;

/** Minimum per-finger (lowerY − upperY) to count as "lower joint is
 *  below upper joint". Calibration 2026-04-23 on the DIP-TIP layer
 *  (cleanest discriminator):
 *    bend_still       p5  = 0.04-0.07  (tip clearly below DIP)
 *    pluck_index_*    p95 = 0.005-0.015 (tip essentially flush)
 *    gliss_index_*    p95 = -0.009     (tip above DIP)
 *  0.02 lands cleanly in the gap: every real bend passes, no real
 *  pluck/gliss pose sneaks past. Previous 0.005 allowed occasional
 *  plucks and sweeps to satisfy a bend layer, causing transient bend
 *  mode commits that interrupted plucks and glisses. */
const LOWER_BELOW_UPPER_MIN = 0.02;
/** How many of the three pluck fingers must pass the lower-below-upper
 *  gate per layer. Requires ALL THREE because the natural pluck stance
 *  is 1 extended + 2 cupped ("1 ngón pluck, 2 ngón cắp xuống"); a 2-of-3
 *  threshold would lock the hand in bend mode during normal play and
 *  the player couldn't exit. Tracking-dropout robustness comes from the
 *  three-layer (DIP/PIP/MCP) voting instead of per-layer slack. */
const BEND_LAYER_MIN_PASS = 3;

/** Upper bound on the upper-joint X-spread for bend — rejects "xòe tè
 *  le" (splayed fingers pointing in different directions). Pinched or
 *  moderate spread is fine; very wide flare is not. */
const BEND_MAX_X_SPREAD = 0.20;

/** Max curl for a finger to still count as "extended enough to pluck".
 *  For pluck mode we require AT LEAST ONE of index/middle/ring to be
 *  this extended (user: "có quyền 2 ngón cúp, 1 ngón pluck"). A hand
 *  with all three curled is messy — not a strike stance. `fingerCurl`
 *  is ~0 when extended, ~0.44 when moderately cupped. */
const PLUCK_EXTENDED_CURL_MAX = 0.25;

/**
 * Bend "layers" — each is an (upper-joint-triplet, lower-joint-triplet)
 * on the 3 play fingers (index/middle/ring). Any ONE layer passing means
 * the hand is in bend posture. Multiple layers means MediaPipe losing
 * the tips (joints 8/12/16 drift off-frame) doesn't kill detection —
 * the more-proximal layer (DIP→PIP or PIP→MCP) still catches the cup.
 */
const BEND_LAYERS: readonly { upper: readonly number[]; lower: readonly number[] }[] = [
  // DIP above TIP
  { upper: [LM.INDEX_DIP, LM.MIDDLE_DIP, LM.RING_DIP], lower: [LM.INDEX_TIP, LM.MIDDLE_TIP, LM.RING_TIP] },
  // PIP above DIP
  { upper: [LM.INDEX_PIP, LM.MIDDLE_PIP, LM.RING_PIP], lower: [LM.INDEX_DIP, LM.MIDDLE_DIP, LM.RING_DIP] },
  // MCP above PIP
  { upper: [LM.INDEX_MCP, LM.MIDDLE_MCP, LM.RING_MCP], lower: [LM.INDEX_PIP, LM.MIDDLE_PIP, LM.RING_PIP] },
] as const;

/** Max Y-spread (max − min of the 3 fingertip Y positions) for pluck to
 *  be armed. Loose because a mid-stroke pluck momentarily misaligns the
 *  tips (one finger descending past the other two); we only use this to
 *  reject truly scrambled poses, not to gate active plucks. Calibration
 *  2026-04-21: pluck_index_slow p95 = 0.259, pluck_index_medium p95 =
 *  0.240. Previous 0.18 gated out valid slow/medium strikes. */
const PLUCK_MAX_Y_SPREAD = 0.28;

/** Fast bend-exit grace when the classifier sees a pluck-pose coming out
 *  of bend — fingers straightened enough that the cup signal dropped.
 *  Short so switching from bend to pluck feels responsive. */
const BEND_EXIT_STRAIGHT_MS = 300;

/** Slower bend-exit grace for non-deliberate drops (z-push gone but
 *  alignment or pose is too scrambled to qualify as pluck — typically a
 *  tracking dropout). Absorbs MediaPipe noise without feeling stuck. */
const BEND_EXIT_OTHER_MS = 500;

export interface ClassifierSettings {
  modeStickinessMs: number;
  /** Grace period before leaving bend — bend needs 3 aligned fingers, so a
   *  single finger slipping off-screen must not instantly drop the bend. */
  bendExitStickinessMs: number;
  /** Max curl for a finger to count as "extended" in the bend posture. */
  bendExtendedThreshold: number;
  /** Max |ΔY| between any pair of (index, middle, ring) tips. */
  bendAlignThreshold: number;
}

export const DEFAULT_CLASSIFIER_SETTINGS: ClassifierSettings = {
  modeStickinessMs: 120,
  bendExitStickinessMs: 700,
  bendExtendedThreshold: 0.40,
  bendAlignThreshold: 0.045,
};

export interface ClassifierInput {
  landmarks: Landmarks | null;
  now: number;
}

export interface ClassifierOutput {
  mode: GestureMode;
  confidence: number;
}

export class GestureClassifier {
  private lastMode: GestureMode = 'idle';
  private pendingMode: GestureMode | null = null;
  private pendingSince = 0;
  /** Slowly-decaying peak palmW on this hand, for the relative depth gate. */
  private peakPalmW = 0;

  constructor(
    public readonly hand: HandId,
    private settings: ClassifierSettings = DEFAULT_CLASSIFIER_SETTINGS,
  ) {}

  updateSettings(s: Partial<ClassifierSettings>): void {
    this.settings = { ...this.settings, ...s };
  }

  classify({ landmarks, now }: ClassifierInput): ClassifierOutput {
    if (!landmarks) {
      this.lastMode = 'idle';
      this.pendingMode = null;
      return { mode: 'idle', confidence: 0 };
    }
    const candidate = this.score(landmarks);
    const mode = this.applyStickiness(candidate.mode, now);
    return { mode, confidence: candidate.confidence };
  }

  // ---------- internals ----------

  private score(lm: Landmarks): ClassifierOutput {
    // --- Orientation gates ----------------------------------------------
    const wrist = lm[LM.WRIST]!;
    const midMcp = lm[LM.MIDDLE_MCP]!;
    const idxMcp = lm[LM.INDEX_MCP]!;
    const pinkyMcp = lm[LM.PINKY_MCP]!;

    const palmW = dist(idxMcp, pinkyMcp);
    const palmH = dist(wrist, midMcp);
    const palmFacingOk = palmH > 1e-6 && palmW / palmH >= PALM_FACING_RATIO;

    const dxWM = Math.abs(midMcp.x - wrist.x);
    const dyWM = wrist.y - midMcp.y;
    const uprightOk = dyWM > 0 && dyWM >= HAND_UPRIGHT_Y_OVER_X * dxWM;

    // Previous palm-front check used `this.hand === 'right' ? X : !X`
    // which was asymmetric vs MediaPipe's handedness labelling and
    // made LH bend very hard to trigger. palmFacingOk + uprightOk
    // are enough to reject the common back-of-hand / sideways poses.
    if (!palmFacingOk || !uprightOk) {
      return { mode: 'idle', confidence: 0 };
    }

    // Running peak palmW — self-calibrates the bend depth gate.
    this.peakPalmW = Math.max(this.peakPalmW * PEAK_PALM_DECAY, palmW);

    // --- BEND -----------------------------------------------------------
    const alignTol = this.settings.bendAlignThreshold * BEND_Y_ALIGN_RELAX;
    let bendConfidence = -1;
    for (const layer of BEND_LAYERS) {
      const u0 = lm[layer.upper[0]!];
      const u1 = lm[layer.upper[1]!];
      const u2 = lm[layer.upper[2]!];
      const l0 = lm[layer.lower[0]!];
      const l1 = lm[layer.lower[1]!];
      const l2 = lm[layer.lower[2]!];
      if (!u0 || !u1 || !u2 || !l0 || !l1 || !l2) continue;
      // Count fingers where the lower joint is at least `LOWER_BELOW_UPPER_MIN`
      // below the upper joint. The layer passes if at least
      // `BEND_LAYER_MIN_PASS` fingers qualify — tolerant of single-
      // finger tracking dropouts while still rejecting neutral hands.
      let below = 0;
      if (l0.y - u0.y > LOWER_BELOW_UPPER_MIN) below++;
      if (l1.y - u1.y > LOWER_BELOW_UPPER_MIN) below++;
      if (l2.y - u2.y > LOWER_BELOW_UPPER_MIN) below++;
      if (below < BEND_LAYER_MIN_PASS) continue;
      const upperMaxY = Math.max(u0.y, u1.y, u2.y);
      const upperMinY = Math.min(u0.y, u1.y, u2.y);
      const upperYSpread = upperMaxY - upperMinY;
      if (upperYSpread >= alignTol) continue;
      const upperMaxX = Math.max(u0.x, u1.x, u2.x);
      const upperMinX = Math.min(u0.x, u1.x, u2.x);
      if (upperMaxX - upperMinX >= BEND_MAX_X_SPREAD) continue;
      bendConfidence = Math.max(bendConfidence, 1 - upperYSpread / alignTol);
    }
    if (bendConfidence >= 0) {
      if (palmW < BEND_DEPTH_FRAC * this.peakPalmW) {
        return { mode: 'idle', confidence: 0 };
      }
      return { mode: 'bend', confidence: bendConfidence };
    }

    // --- PLUCK ready pose -----------------------------------------------
    const idxTip = lm[FINGER_TIPS.index];
    const midTip = lm[FINGER_TIPS.middle];
    const ringTip = lm[FINGER_TIPS.ring];
    if (!idxTip || !midTip || !ringTip) {
      return { mode: 'idle', confidence: 0 };
    }
    const tipMaxX = Math.max(idxTip.x, midTip.x, ringTip.x);
    const tipMinX = Math.min(idxTip.x, midTip.x, ringTip.x);
    const tipXSpread = tipMaxX - tipMinX;
    if (tipXSpread < PLAY_MIN_X_SPREAD) {
      return { mode: 'idle', confidence: 0 };
    }
    const tipMaxY = Math.max(idxTip.y, midTip.y, ringTip.y);
    const tipMinY = Math.min(idxTip.y, midTip.y, ringTip.y);
    if (tipMaxY - tipMinY >= PLUCK_MAX_Y_SPREAD) {
      return { mode: 'idle', confidence: 0 };
    }
    // "có quyền 2 ngón cúp, 1 ngón pluck" — at least ONE of I/M/R must
    // be extended.
    const minPluckCurl = Math.min(
      fingerCurl(lm, 'index'),
      fingerCurl(lm, 'middle'),
      fingerCurl(lm, 'ring'),
    );
    if (minPluckCurl >= PLUCK_EXTENDED_CURL_MAX) {
      return { mode: 'idle', confidence: 0 };
    }
    return { mode: 'pluck', confidence: Math.min(1, tipXSpread * 5) };
  }

  private applyStickiness(candidate: GestureMode, now: number): GestureMode {
    if (candidate === this.lastMode) {
      this.pendingMode = null;
      return this.lastMode;
    }
    if (this.pendingMode !== candidate) {
      this.pendingMode = candidate;
      this.pendingSince = now;
    }
    // Bend has two exit graces:
    //   bend → pluck   : user visibly straightened fingers (deliberate) → 300ms
    //   bend → other   : z-push / alignment / depth dropped but posture
    //                    isn't a clear pluck-pose (could be tracking noise) → 500ms
    // Everything else (entering modes, mute, etc.) uses modeStickinessMs.
    let holdMs: number;
    if (this.lastMode === 'bend') {
      holdMs = candidate === 'pluck' ? BEND_EXIT_STRAIGHT_MS : BEND_EXIT_OTHER_MS;
    } else {
      holdMs = this.settings.modeStickinessMs;
    }
    if (now - this.pendingSince >= holdMs) {
      this.lastMode = candidate;
      this.pendingMode = null;
    }
    return this.lastMode;
  }
}
